import { BellIcon, CalendarIcon } from '@heroicons/react/outline';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { CardTitle } from 'shared-ui/lib/components/CardTitle';
import { GenericIndexScene } from 'shared-ui/lib/components/table/TableX';
import { setupQueryConfig } from 'shared-ui/lib/config/query.config';
import { dashboardFaultColumns } from '../../columns/dashboard-fault.columns';
import { dashboardReminderColumns } from '../../columns/dashboard-reminder.columns';
import { ServiceCallDashboardFilter } from '../../form/service-call-dashboard-filter/ServiceCallDashboardFilter';
import {
  SERVICE_CALL_DASHBOARD_TAB_PARAM,
  ServiceCallDashboardFilterValues,
  ServiceCallDashboardTab,
  buildServiceCallDashboardWhere,
  defaultServiceCallDashboardFilter,
  formatServiceCallDashboardTabLabel,
  parseServiceCallDashboardTab,
} from '../../form/service-call-dashboard-filter/service-call-dashboard-filter.form';
import {
  usePartnerDashboardFaultTabCountQuery,
  usePartnerDashboardFaultsLazyQuery,
  usePartnerDashboardOverdueRemindersQuery,
  usePartnerDashboardReminderTabCountQuery,
  usePartnerDashboardRemindersLazyQuery,
} from '../../generated/graphql';

const tabClassName = (active: boolean): string =>
  `appearance-none flex-1 inline-flex items-center justify-center px-6 py-3 text-base font-semibold border-0 border-b-4 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 ${
    active
      ? 'bg-white text-primary border-primary'
      : 'bg-gray-100 text-gray-color border-transparent hover:bg-gray-200 hover:text-gray-900'
  }`;

export const DashboardScene = (): JSX.Element => {
  const { t } = useTranslation('ServiceCallDashboardScene');
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseServiceCallDashboardTab(searchParams.get(SERVICE_CALL_DASHBOARD_TAB_PARAM));
  const [filterValues, setFilterValues] = useState<ServiceCallDashboardFilterValues>(
    defaultServiceCallDashboardFilter()
  );

  const setTab = (nextTab: ServiceCallDashboardTab) => {
    setSearchParams((current) => {
      const params = new URLSearchParams(current);

      if (nextTab === 'faults') {
        params.set(SERVICE_CALL_DASHBOARD_TAB_PARAM, 'faults');
      } else {
        params.delete(SERVICE_CALL_DASHBOARD_TAB_PARAM);
      }

      return params;
    });
  };

  const remindersQuery = usePartnerDashboardRemindersLazyQuery(setupQueryConfig());
  const faultsQuery = usePartnerDashboardFaultsLazyQuery(setupQueryConfig());
  const overdueRemindersQuery = usePartnerDashboardOverdueRemindersQuery(setupQueryConfig());
  const reminderTabCountQuery = usePartnerDashboardReminderTabCountQuery(setupQueryConfig());
  const faultTabCountQuery = usePartnerDashboardFaultTabCountQuery(setupQueryConfig());
  const where = buildServiceCallDashboardWhere(filterValues, tab);
  const resultCount =
    tab === 'reminders'
      ? remindersQuery[1].data?.partnerDashboardReminders.totalCount
      : faultsQuery[1].data?.partnerDashboardFaults.totalCount;
  const reminderTotal = reminderTabCountQuery.data?.partnerDashboardReminders.totalCount ?? 0;
  const faultTotal = faultTabCountQuery.data?.partnerDashboardFaults.totalCount ?? 0;
  const overdueCount = (overdueRemindersQuery.data?.partnerDashboardReminders.list ?? []).filter(
    (reminder) => moment().isSameOrAfter(moment(reminder.date))
  ).length;

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="flex border-b border-gray-200" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'reminders'}
          className={tabClassName(tab === 'reminders')}
          onClick={() => setTab('reminders')}
        >
          <CalendarIcon className="h-5 w-5 mr-2 flex-shrink-0" aria-hidden="true" />
          <span>{formatServiceCallDashboardTabLabel(t('tabReminders'), reminderTotal)}</span>
          {overdueCount > 0 ? (
            <span
              className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-color text-white text-xs font-bold leading-none flex-shrink-0"
              aria-label={`${overdueCount} ${t('statusOverdue')}`}
            >
              {overdueCount}
            </span>
          ) : undefined}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'faults'}
          className={tabClassName(tab === 'faults')}
          onClick={() => setTab('faults')}
        >
          <BellIcon className="h-5 w-5 mr-2 flex-shrink-0" aria-hidden="true" />
          <span>{formatServiceCallDashboardTabLabel(t('tabFaults'), faultTotal)}</span>
        </button>
      </div>
      <div className="p-4">
        <CardTitle>{tab === 'reminders' ? t('tabReminders') : t('tabFaults')}</CardTitle>
        <div className="mb-4">
          <ServiceCallDashboardFilter
            tab={tab}
            appliedValues={filterValues}
            resultCount={resultCount}
            onApply={setFilterValues}
          />
        </div>
        {tab === 'reminders' ? (
          <GenericIndexScene
            key="reminders"
            unwrapped
            perPage={30}
            dataPath="partnerDashboardReminders.list"
            countPath="partnerDashboardReminders.totalCount"
            query={remindersQuery}
            where={where}
            defaultOrderBy={{ date: 'asc' }}
            columns={[
              dashboardReminderColumns.businessName,
              dashboardReminderColumns.vehicle,
              dashboardReminderColumns.title,
              dashboardReminderColumns.date,
              dashboardReminderColumns.status,
              dashboardReminderColumns.linkToVehicle,
            ]}
          />
        ) : (
          <GenericIndexScene
            key="faults"
            unwrapped
            perPage={30}
            dataPath="partnerDashboardFaults.list"
            countPath="partnerDashboardFaults.totalCount"
            query={faultsQuery}
            where={where}
            defaultOrderBy={{ createdAt: 'desc' }}
            columns={[
              dashboardFaultColumns.businessName,
              dashboardFaultColumns.vehicle,
              dashboardFaultColumns.description,
              dashboardFaultColumns.createdAt,
              dashboardFaultColumns.status,
              dashboardFaultColumns.linkToFault,
            ]}
          />
        )}
      </div>
    </div>
  );
};
